import type { ReactNode } from 'react'
import React from 'react'

import { Icon, PriceString, Tooltip } from '@fourthwall/components'
import { QuestionMarkIcon } from '@fourthwall/icons'
import type { BillingOrderCostsBreakdownEntry } from '@fourthwall/services/dist/BillingApiClient'

import * as S from './BreakdownSection.styled'

export type Props = {
  title: string
  cost?: ReactNode
  overriddenCost?: ReactNode
  isCostFluid?: boolean
  tooltipContent?: string
  costTooltipContent?: string
  entries?: BillingOrderCostsBreakdownEntry[]
}

const infoIcon = <Icon component={QuestionMarkIcon} height={18} />

export const BreakdownSection = ({
  title,
  cost,
  overriddenCost,
  isCostFluid,
  tooltipContent,
  costTooltipContent,
  entries,
}: Props) => {
  const renderTootltip = () => {
    if (!tooltipContent) {
      return null
    }

    return (
      <S.TooltipWrapper>
        <Tooltip reference={infoIcon} placement="top" fluid>
          {tooltipContent}
        </Tooltip>
      </S.TooltipWrapper>
    )
  }

  const renderEntries = () => {
    if (!entries?.length) {
      return
    }

    return (
      <S.ItemsContainer>
        {entries.map((entry) => (
          <S.Item key={entry.title}>
            <S.ItemContent>
              <S.Title>
                <S.TitleText>{entry.title}</S.TitleText>
              </S.Title>
              <S.Quantity>{entry.quantity}x</S.Quantity>
              <S.Cost>
                {entry.overriddenCost?.value && (
                  <S.OverriddenCost>
                    <PriceString value={entry.overriddenCost.value} negative />
                  </S.OverriddenCost>
                )}
                <PriceString value={entry.cost.value} negative />
              </S.Cost>
            </S.ItemContent>
            {entry.subtitle && <S.Subtitle>{entry.subtitle}</S.Subtitle>}
          </S.Item>
        ))}
      </S.ItemsContainer>
    )
  }

  return (
    <S.Container>
      <S.Content>
        <S.Title>
          <S.TitleText>{title}</S.TitleText> {renderTootltip()}
        </S.Title>
        {cost && (
          <S.Cost fluid={isCostFluid}>
            {overriddenCost && <S.OverriddenCost>{overriddenCost}</S.OverriddenCost>}
            {cost}
            {costTooltipContent && (
              <S.TooltipWrapper>
                <Tooltip reference={infoIcon} placement="left" fluid>
                  {costTooltipContent}
                </Tooltip>
              </S.TooltipWrapper>
            )}
          </S.Cost>
        )}
      </S.Content>
      {renderEntries()}
    </S.Container>
  )
}
